import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Row, Col, Card, Table, Button, Input, Modal, notification, Space, Tag, Typography, Spin,
  Tabs, Upload, Form, Tooltip, Popconfirm,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SendOutlined,
  ExclamationCircleOutlined, SearchOutlined, ReloadOutlined, EyeOutlined,
  PlayCircleOutlined, FileOutlined, UploadOutlined, FileExcelOutlined,
  DownloadOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import {
  listCampaigns, deleteCampaign, previewCampaign,
  listExcelLists, uploadExcelList, deleteExcelList,
  getExcelListData, updateExcelListData, downloadExcelList,
} from './festivalCampaignService';
import './FestivalCampaign.css';

const { confirm } = Modal;
const { Title, Text } = Typography;

const STATUS_COLOR = { draft: 'default', active: 'blue', sent: 'green' };
const MEDIA_ICON = { video: <PlayCircleOutlined />, document: <FileOutlined /> };

// ── Excel Editor Modal ────────────────────────────────────────────────────────
function ExcelEditorModal({ list, visible, onClose, onSaved }) {
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [editHeaders, setEditHeaders] = useState([]);
  const [editRows, setEditRows]       = useState([]);
  const [editingCell, setEditingCell]       = useState(null); // { rowKey, col }
  const [pendingValue, setPendingValue]     = useState('');
  const [editingHeader, setEditingHeader]   = useState(null); // column name being renamed
  const [headerPending, setHeaderPending]   = useState('');
  const [hasChanges, setHasChanges]         = useState(false);
  const [downloading, setDownloading]       = useState(false);

  const loadData = useCallback(async () => {
    if (!list) return;
    setLoading(true);
    setHasChanges(false);
    setEditingCell(null);
    try {
      const res = await getExcelListData(list.id);
      if (res.status) {
        setEditHeaders(res.headers);
        setEditRows(res.rows.map((row, i) => ({ ...row, _key: i })));
      } else {
        notification.error({ message: res.message || 'Failed to load data' });
      }
    } catch (err) {
      notification.error({ message: 'Failed to load Excel data', description: err.message });
    } finally {
      setLoading(false);
    }
  }, [list]);

  useEffect(() => {
    if (visible && list) loadData();
  }, [visible, list]); // eslint-disable-line

  const commitEdit = (rowKey, col) => {
    setEditRows(prev => prev.map(r => r._key === rowKey ? { ...r, [col]: pendingValue } : r));
    setEditingCell(null);
    setPendingValue('');
    setHasChanges(true);
  };

  const handleDeleteRow = (rowKey) => {
    setEditRows(prev => prev.filter(r => r._key !== rowKey));
    setHasChanges(true);
  };

  const handleDeleteColumn = (col) => {
    Modal.confirm({
      title: `Remove column "${col}"?`,
      content: 'This will remove this column from all rows. You can restore it by re-uploading the original file.',
      okText: 'Remove',
      okType: 'danger',
      onOk: () => {
        setEditHeaders(prev => prev.filter(h => h !== col));
        setEditRows(prev => prev.map(r => { const { [col]: _removed, ...rest } = r; return rest; })); // eslint-disable-line
        setHasChanges(true);
      },
    });
  };

  const commitHeaderRename = (oldCol) => {
    const newCol = headerPending.trim();
    setEditingHeader(null);
    setHeaderPending('');
    if (!newCol || newCol === oldCol) return;
    if (editHeaders.includes(newCol)) {
      notification.error({ message: `Column "${newCol}" already exists` });
      return;
    }
    // Rename key in every row
    setEditRows(prev => prev.map(r => {
      const { [oldCol]: val, ...rest } = r; // eslint-disable-line
      return { ...rest, [newCol]: val };
    }));
    setEditHeaders(prev => prev.map(h => (h === oldCol ? newCol : h)));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanRows = editRows.map(({ _key, ...rest }) => rest); // eslint-disable-line
      const res = await updateExcelListData(list.id, editHeaders, cleanRows);
      if (res.status) {
        notification.success({
          message: 'Excel list saved!',
          description: `${res.valid_contacts.toLocaleString('en-IN')} valid contacts across ${res.total_rows.toLocaleString('en-IN')} rows.`,
        });
        setHasChanges(false);
        onSaved?.();
      } else {
        notification.error({ message: res.message || 'Save failed' });
      }
    } catch (err) {
      notification.error({ message: 'Save error', description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadExcelList(list.id, list.name);
    } catch (err) {
      notification.error({ message: 'Download failed', description: err.message });
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      Modal.confirm({
        title: 'Discard unsaved changes?',
        icon: <ExclamationCircleOutlined />,
        okText: 'Discard',
        okType: 'danger',
        onOk: () => { setHasChanges(false); onClose(); },
      });
    } else {
      onClose();
    }
  };

  // Rough count of valid contacts for live display
  const validCount = editRows.filter(r => {
    const phone = String(
      r.phone || r.phone_no || r.mobile || r.mobile_no || r.number || r['मोबाइल'] || r['फ़ोन'] || r['नंबर'] || ''
    ).replace(/\D/g, '');
    return phone.length >= 10;
  }).length;

  const tableColumns = [
    {
      title: '#', width: 55, align: 'center', fixed: 'left',
      render: (_, __, idx) => <span style={{ color: '#999', fontSize: 11 }}>{idx + 1}</span>,
    },
    ...editHeaders.map(col => ({
      key: col,
      dataIndex: col,
      width: col === 'phone' || col === 'phone_no' || col === 'mobile' ? 130 : 160,
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
          {editingHeader === col ? (
            <Input
              autoFocus
              size="small"
              value={headerPending}
              onChange={e => setHeaderPending(e.target.value)}
              onBlur={() => commitHeaderRename(col)}
              onPressEnter={() => commitHeaderRename(col)}
              onKeyDown={e => e.key === 'Escape' && (setEditingHeader(null), setHeaderPending(''))}
              onClick={e => e.stopPropagation()}
              style={{ fontSize: 12, width: 110, fontWeight: 600 }}
            />
          ) : (
            <Tooltip title="Click to rename column">
              <span
                style={{ fontWeight: 600, fontSize: 12, cursor: 'text', borderBottom: '1px dashed #aaa', paddingBottom: 1 }}
                onClick={(e) => { e.stopPropagation(); setEditingHeader(col); setHeaderPending(col); }}
              >
                {col}
              </span>
            </Tooltip>
          )}
          <Tooltip title={`Remove column "${col}"`}>
            <CloseCircleOutlined
              onClick={(e) => { e.stopPropagation(); handleDeleteColumn(col); }}
              style={{ color: '#ff4d4f', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}
            />
          </Tooltip>
        </div>
      ),
      render: (val, record) => {
        const isEditing = editingCell?.rowKey === record._key && editingCell?.col === col;
        if (isEditing) {
          return (
            <Input
              autoFocus
              size="small"
              value={pendingValue}
              onChange={e => setPendingValue(e.target.value)}
              onBlur={() => commitEdit(record._key, col)}
              onPressEnter={() => commitEdit(record._key, col)}
              onKeyDown={e => e.key === 'Escape' && setEditingCell(null)}
              style={{ minWidth: 80 }}
            />
          );
        }
        const display = val !== '' && val != null ? String(val) : '';
        return (
          <div
            onClick={() => { setEditingCell({ rowKey: record._key, col }); setPendingValue(display); }}
            style={{ minHeight: 22, cursor: 'text', padding: '1px 4px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={display}
          >
            {display || <span style={{ color: '#d9d9d9', fontSize: 12 }}>—</span>}
          </div>
        );
      },
    })),
    {
      title: '', key: '_del', width: 50, align: 'center', fixed: 'right',
      render: (_, record) => (
        <Popconfirm
          title="Delete this row?"
          okType="danger"
          okText="Delete"
          onConfirm={() => handleDeleteRow(record._key)}
          placement="left"
        >
          <Button type="text" size="small" icon={<DeleteOutlined />} danger />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined style={{ color: '#52c41a' }} />
          <span>Edit Excel List: <b>{list?.name}</b></span>
          {hasChanges && <Tag color="orange" style={{ marginLeft: 4 }}>Unsaved changes</Tag>}
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width="94vw"
      style={{ top: 16 }}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Tag color="blue">{editRows.length.toLocaleString('en-IN')} rows</Tag>
            <Tag color="green">{validCount.toLocaleString('en-IN')} valid contacts</Tag>
            <Tag>{editHeaders.length} columns</Tag>
          </Space>
          <Space>
            <Button icon={<DownloadOutlined />} loading={downloading} onClick={handleDownload}>
              Download
            </Button>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              loading={saving}
              onClick={handleSave}
              disabled={!hasChanges}
            >
              Save Changes
            </Button>
          </Space>
        </div>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size="large" tip="Loading Excel data..." />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            Click any <b>cell</b> to edit its value. Click a <b style={{ borderBottom: '1px dashed #aaa' }}>column name</b> to rename it.
            Use the <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> icon to remove a column. Use the <DeleteOutlined style={{ color: '#ff4d4f' }} /> button to remove a row.
          </div>
          <Table
            dataSource={editRows}
            columns={tableColumns}
            rowKey="_key"
            size="small"
            bordered
            scroll={{ x: 'max-content', y: 'calc(80vh - 220px)' }}
            pagination={{
              pageSize: 100,
              showSizeChanger: true,
              pageSizeOptions: ['50', '100', '200', '500'],
              showTotal: (t) => `${t} rows`,
            }}
          />
        </>
      )}
    </Modal>
  );
}

// ── Campaigns Tab ────────────────────────────────────────────────────────────
function CampaignsTab() {
  const history = useHistory();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [preview, setPreview] = useState({ visible: false, loading: false, url: null, type: 'image', name: '' });

  const fetchList = async (page = 1, pageSize = 20, s = search) => {
    setLoading(true);
    try {
      const res = await listCampaigns({ page, limit: pageSize, search: s });
      if (res.status) {
        setData(res.result.map((r, i) => ({ ...r, key: r.id, sno: (page - 1) * pageSize + i + 1 })));
        setPagination({ current: page, pageSize, total: res.pagination?.total || 0 });
      }
    } catch (err) {
      notification.error({ message: 'Failed to load campaigns', description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const handleSearch = (value) => {
    setSearch(value);
    fetchList(1, pagination.pageSize, value);
  };

  const handleDelete = (id) => {
    confirm({
      title: 'Delete this campaign?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes, Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteCampaign(id);
          notification.success({ message: 'Campaign deleted' });
          fetchList(pagination.current, pagination.pageSize);
        } catch (err) {
          notification.error({ message: 'Delete failed', description: err.message });
        }
      },
    });
  };

  const handlePreview = async (record) => {
    setPreview({ visible: true, loading: true, url: null, type: record.media_type || 'image', name: record.name });
    try {
      const res = await previewCampaign(record.id);
      if (res.status) {
        setPreview(prev => ({ ...prev, loading: false, url: res.preview_url, type: res.media_type || record.media_type || 'image' }));
      } else {
        notification.error({ message: res.message || 'Preview failed' });
        setPreview(prev => ({ ...prev, loading: false, visible: false }));
      }
    } catch (err) {
      notification.error({ message: 'Preview error', description: err.message });
      setPreview(prev => ({ ...prev, loading: false, visible: false }));
    }
  };

  const columns = [
    { title: '#', dataIndex: 'sno', width: 60, align: 'center' },
    { title: 'Campaign Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Festival Type', dataIndex: 'festival_type', key: 'festival_type',
      render: (v) => <Tag>{v}</Tag>
    },
    {
      title: 'Media', dataIndex: 'media_type', key: 'media_type',
      width: 90,
      render: (v) => (
        <Tag icon={MEDIA_ICON[v]} color={v === 'image' ? 'geekblue' : v === 'video' ? 'purple' : 'orange'}>
          {v || 'image'}
        </Tag>
      )
    },
    { title: 'Template', dataIndex: 'template_name', key: 'template_name' },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (v) => <Tag color={STATUS_COLOR[v] || 'default'}>{v?.toUpperCase()}</Tag>
    },
    {
      title: 'Created', dataIndex: 'created_at', key: 'created_at',
      render: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '-'
    },
    {
      title: 'Actions', key: 'actions', align: 'center', width: 240,
      render: (_, record) => (
        <Space>
          <Button
            size="small" icon={<EyeOutlined />} title="Preview"
            onClick={() => handlePreview(record)}
          >View</Button>
          <Button
            size="small" icon={<EditOutlined />}
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
            onClick={() => history.push(`/festival-campaign/edit/${record.id}`)}
          >Edit</Button>
          <Button
            size="small" icon={<SendOutlined />} style={{ color: '#52c41a', borderColor: '#52c41a' }}
            onClick={() => history.push(`/festival-campaign/send/${record.id}`)}
          >Send</Button>
          <Button
            size="small" icon={<DeleteOutlined />} danger
            onClick={() => handleDelete(record.id)}
          >Delete</Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Space>
          <Input.Search
            placeholder="Search campaigns..."
            allowClear
            onSearch={handleSearch}
            style={{ width: 220 }}
            prefix={<SearchOutlined />}
          />
          <Button icon={<ReloadOutlined />} onClick={() => fetchList()}>Refresh</Button>
          <Button
            icon={<PlusOutlined />}
            onClick={() => history.push('/festival-campaign/add')}
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
          >
            New Campaign
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        scroll={{ x: 900 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} campaigns`,
          onChange: (page, pageSize) => fetchList(page, pageSize),
        }}
      />

      {/* Preview Modal */}
      <Modal
        title={`Preview — ${preview.name}`}
        open={preview.visible}
        onCancel={() => setPreview(prev => ({ ...prev, visible: false, url: null }))}
        footer={null}
        centered
        width={preview.type === 'image' ? 520 : 480}
        destroyOnClose
      >
        {preview.loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" tip="Generating preview..." />
          </div>
        ) : preview.url ? (
          <>
            {preview.type === 'image' && (
              <img
                src={preview.url}
                alt="Festival Poster Preview"
                style={{ width: '100%', borderRadius: 8, display: 'block' }}
              />
            )}
            {preview.type === 'video' && (
              <video
                src={preview.url}
                controls
                style={{ width: '100%', borderRadius: 8 }}
              />
            )}
            {preview.type === 'document' && (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <FileOutlined style={{ fontSize: 48, color: '#fa8c16' }} />
                <p style={{ marginTop: 12 }}>Document file — click below to open</p>
                <a href={preview.url} target="_blank" rel="noopener noreferrer">
                  <Button type="primary">Open Document</Button>
                </a>
              </div>
            )}
          </>
        ) : null}
      </Modal>
    </>
  );
}

// ── Excel Lists Tab ───────────────────────────────────────────────────────────
function ExcelListsTab() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);

  // Upload modal state
  const [uploadVisible, setUploadVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');

  // Editor modal state
  const [editorList, setEditorList]       = useState(null);
  const [editorVisible, setEditorVisible] = useState(false);

  // Per-row download loading
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const res = await listExcelLists();
      if (res.status) {
        setLists(res.result.map((r, i) => ({ ...r, key: r.id, sno: i + 1 })));
      }
    } catch (err) {
      notification.error({ message: 'Failed to load Excel lists', description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLists(); }, []);

  const handleDelete = (record) => {
    confirm({
      title: `Delete list "${record.name}"?`,
      content: 'The uploaded Excel file will also be removed from disk.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes, Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteExcelList(record.id);
          notification.success({ message: 'Excel list deleted' });
          fetchLists();
        } catch (err) {
          notification.error({ message: 'Delete failed', description: err.message });
        }
      },
    });
  };

  const handleDownload = async (record) => {
    setDownloadingId(record.id);
    try {
      await downloadExcelList(record.id, record.name);
    } catch (err) {
      notification.error({ message: 'Download failed', description: err.message });
    } finally {
      setDownloadingId(null);
    }
  };

  const openEditor = (record) => {
    setEditorList(record);
    setEditorVisible(true);
  };

  const handleUpload = async () => {
    if (!uploadName.trim()) {
      notification.warning({ message: 'Please enter a list name' });
      return;
    }
    if (!uploadFile) {
      notification.warning({ message: 'Please select an Excel file' });
      return;
    }
    setUploadLoading(true);
    try {
      const res = await uploadExcelList(uploadName.trim(), uploadFile);
      if (res.status) {
        notification.success({
          message: 'Excel list saved!',
          description: `${res.result.valid_contacts} valid contacts out of ${res.result.total_rows} rows.`,
        });
        setUploadVisible(false);
        setUploadFile(null);
        setUploadName('');
        fetchLists();
      } else {
        notification.error({ message: res.message || 'Upload failed' });
      }
    } catch (err) {
      notification.error({ message: 'Upload error', description: err.message });
    } finally {
      setUploadLoading(false);
    }
  };

  const renderColumns = (cols) => {
    if (!cols) return '-';
    let parsed = cols;
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { return cols; }
    }
    if (!Array.isArray(parsed) || !parsed.length) return '-';
    const visible = parsed.slice(0, 3);
    const extra = parsed.length - 3;
    return (
      <Space size={4} wrap>
        {visible.map(c => <Tag key={c} color="blue" style={{ marginBottom: 2 }}>{c}</Tag>)}
        {extra > 0 && <Tag color="default">+{extra} more</Tag>}
      </Space>
    );
  };

  const columns = [
    { title: '#', dataIndex: 'sno', width: 55, align: 'center' },
    { title: 'List Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Columns Detected', dataIndex: 'detected_columns', key: 'detected_columns',
      render: (v) => renderColumns(v),
    },
    {
      title: 'Total Rows', dataIndex: 'total_rows', key: 'total_rows', width: 110, align: 'right',
      render: (v) => v?.toLocaleString('en-IN') || 0,
    },
    {
      title: 'Valid Contacts', dataIndex: 'valid_contacts', key: 'valid_contacts', width: 130, align: 'right',
      render: (v) => <Tag color="green">{v?.toLocaleString('en-IN') || 0}</Tag>,
    },
    {
      title: 'Uploaded', dataIndex: 'created_at', key: 'created_at', width: 130,
      render: (v) => v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
    },
    {
      title: 'Actions', key: 'actions', align: 'center', width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="View / Edit rows & columns">
            <Button
              size="small" icon={<EditOutlined />}
              style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
              onClick={() => openEditor(record)}
            >
              Edit
            </Button>
          </Tooltip>
          <Tooltip title="Download Excel file">
            <Button
              size="small" icon={<DownloadOutlined />}
              loading={downloadingId === record.id}
              onClick={() => handleDownload(record)}
            >Download</Button>
          </Tooltip>
          <Tooltip title="Delete list">
            <Button size="small" icon={<DeleteOutlined />} danger onClick={() => handleDelete(record)}>Delete</Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchLists}>Refresh</Button>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setUploadVisible(true)}
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
          >
            Upload New List
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={lists}
        loading={loading}
        rowKey="id"
        scroll={{ x: 800 }}
        pagination={{ pageSize: 20, showTotal: (t) => `Total ${t} lists` }}
      />

      {/* Upload Modal */}
      <Modal
        title={<Space><FileExcelOutlined style={{ color: '#52c41a' }} /><span>Upload New Excel Contact List</span></Space>}
        open={uploadVisible}
        onCancel={() => { setUploadVisible(false); setUploadFile(null); setUploadName(''); }}
        onOk={handleUpload}
        okText="Save List"
        confirmLoading={uploadLoading}
        destroyOnClose
        width={520}
      >
        <Form layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="List Name" required>
            <Input
              placeholder="e.g. जोधपुर जिला लिस्ट 2026"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              maxLength={200}
            />
          </Form.Item>

          <Form.Item label="Excel File (.xlsx / .xls)" required>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={(file) => { setUploadFile(file); return false; }}
              onRemove={() => setUploadFile(null)}
              fileList={uploadFile ? [{ uid: '1', name: uploadFile.name, status: 'done' }] : []}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>

          {uploadFile && (
            <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '8px 12px' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <FileExcelOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                <b>{uploadFile.name}</b> selected. The file will be saved on the server and parsed to detect columns and count contacts.
              </Text>
            </div>
          )}

          <div style={{ marginTop: 12, background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, padding: '8px 12px' }}>
            <Text style={{ fontSize: 12 }}>
              <b>Recognised column names:</b><br />
              <Tag color="red">phone</Tag> / mobile / phone_no / mobile_no / number<br />
              <Tag color="blue">name</Tag> village / city / district / tehsil / address / occupation / father_name / relation
            </Text>
          </div>
        </Form>
      </Modal>

      {/* Excel Editor Modal */}
      <ExcelEditorModal
        list={editorList}
        visible={editorVisible}
        onClose={() => setEditorVisible(false)}
        onSaved={() => { fetchLists(); }}
      />
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FestivalCampaignList() {
  return (
    <div className="layout-content">
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title={<Title level={4} style={{ margin: 0, color: '#fff' }}>Festival Campaigns</Title>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
          >
            <Tabs
              defaultActiveKey="campaigns"
              items={[
                {
                  key: 'campaigns',
                  label: <span><SendOutlined /> Campaigns</span>,
                  children: <CampaignsTab />,
                },
                {
                  key: 'excel-lists',
                  label: <span><FileExcelOutlined /> Excel Contact Lists</span>,
                  children: <ExcelListsTab />,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
